import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React, { useState, useMemo, useCallback } from 'react';
import useActiveElement from '../hooks/useActiveElement.js';
import FocusTrap from '../utils/FocusTrap.js';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';
import Card from './Card.js';
import Overlay from './Overlay.js';

var _excluded = ["className", "content", "children", "data-testid", "isVisible", "location", "type", "size", "onRequestClose", "onRequestOpen"];
var Popover = function Popover(_ref) {
  var _classnames, _classnames2;
  var className = _ref.className,
    content = _ref.content,
    children = _ref.children,
    dataTestId = _ref['data-testid'],
    isVisible = _ref.isVisible,
    _ref$location = _ref.location,
    location = _ref$location === void 0 ? 'top' : _ref$location,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'hoverAndClick' : _ref$type,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'normal' : _ref$size,
    onRequestClose = _ref.onRequestClose,
    onRequestOpen = _ref.onRequestOpen,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  alertUserOfDeprecatedProps('Popover', otherProps);
  if (otherProps.width) {
    console.error("Hey Canvas User! The width property is no longer supported in the Popover component. Try size=\"dense, normal, or expanded\" instead.");
  }
  if (otherProps.openOn) {
    console.error("Hey Canvas User! The openOn property is no longer supported in the Popover component. Try type=\"hoverandClick or clickOnly\" instead.");
  }
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isPopoverOpen = _useState2[0],
    setIsPopoverOpen = _useState2[1];

  // needed for external override of internal visibility state
  var clickablePopoverState = useMemo(function () {
    if (isVisible !== undefined) {
      return isVisible;
    }
    return isPopoverOpen;
  }, [isVisible, isPopoverOpen]);

  // Cache the current focused dom element across re-renders
  var focusedElRef = useActiveElement(clickablePopoverState);
  var handleClickableOpen = useCallback(function () {
    isVisible === undefined && location !== 'none' && setIsPopoverOpen(true);
    onRequestOpen && onRequestOpen();
  }, [isVisible, location, onRequestOpen]);
  var handleClickableClose = function handleClickableClose() {
    isVisible === undefined && location !== 'none' && setIsPopoverOpen(false);
    onRequestClose && onRequestClose();
  };
  var finalParentClasses = classnames('C-Popover', (_classnames = {}, _defineProperty(_classnames, "--type-".concat(type), type), _defineProperty(_classnames, "isDisabled", isVisible === false || location === 'none'), _classnames), className);
  var finalClasses = classnames('C-Popover__content hc-pa-normal', (_classnames2 = {}, _defineProperty(_classnames2, "--location-".concat(location), location), _defineProperty(_classnames2, "--size-".concat(size), size), _defineProperty(_classnames2, "isVisible", clickablePopoverState), _classnames2), className);
  var isClickOnly = type === 'clickOnly';
  return /*#__PURE__*/React.createElement("span", {
    className: "C-Popover__parent"
  }, /*#__PURE__*/React.createElement("div", _extends({
    "data-testid": dataTestId ? "".concat(dataTestId, "_parent") : null,
    as: isClickOnly ? 'button' : 'div',
    onClick: handleClickableOpen,
    className: finalParentClasses,
    tabIndex: "1"
  }, otherProps), children), /*#__PURE__*/React.createElement(Card, {
    "data-testid": dataTestId ? dataTestId : null,
    className: finalClasses,
    elevation: "3",
    hidden: isClickOnly ? !clickablePopoverState // use "hidden" attr for click-only popovers
    : false
  }, /*#__PURE__*/React.createElement(FocusTrap, {
    as: "div",
    active: clickablePopoverState,
    returnFocusTo: focusedElRef.current,
    onEscape: handleClickableClose,
    "aria-label": otherProps['aria-label'] || 'Popover Content'
  }, content)), /*#__PURE__*/React.createElement(Overlay, {
    onClick: handleClickableClose,
    isVisible: clickablePopoverState
  }));
};
Popover.displayName = 'Popover';

export { Popover as default };
